import { FormEvent, useState } from 'react';

import { Input } from '@/components/form/Input';
import { Inputs } from '@/components/form/Inputs';
import { Modal } from '@/components/Modal';
import { useModifyAccount } from '@/hooks';
import { Account } from '@/types';
import { ROLE_OPTIONS } from '@/utils/role.util';

interface SSOEditModalProps {
  /** The SSO account to edit, or null to close */
  ssoAccount: Account | null;
  /** Called when the modal should close */
  onClose: () => void;
  /** Email context for impersonation (passed to useModifyAccount) */
  email?: string;
}

/**
 * Modal for editing SSO provider settings (Default Role + Role Claim).
 *
 * Only edits DynamoDB settings — does not touch Cognito identity provider config.
 * Merges new settings with existing settings to preserve other fields.
 *
 * Uses a key on the inner form to reset local state when ssoAccount changes,
 * avoiding useEffect + setState which violates React best practices.
 *
 * Used by: SSOProviderSection, SSOProviders, SSOSetupForm
 */
export function SSOEditModal({
  ssoAccount,
  onClose,
  email,
}: SSOEditModalProps) {
  // Key ensures form state resets when editing a different SSO account
  const formKey = ssoAccount?.key ?? '';

  return (
    <Modal
      open={!!ssoAccount}
      onClose={onClose}
      title={`Edit SSO Provider: ${ssoAccount?.member?.split(':')[1] || ''}`}
      size="lg"
      footer={{
        form: 'sso-edit-form',
        text: 'Save',
      }}
      data-testid="sso-edit-modal"
    >
      {ssoAccount && (
        <SSOEditFormContent
          key={formKey}
          ssoAccount={ssoAccount}
          onClose={onClose}
          email={email}
        />
      )}
    </Modal>
  );
}

function SSOEditFormContent({
  ssoAccount,
  onClose,
  email,
}: {
  ssoAccount: Account;
  onClose: () => void;
  email?: string;
}) {
  const settings = ssoAccount.settings;

  const [defaultRole, setDefaultRole] = useState(
    () => settings?.default_sso_role || 'readonly'
  );
  const [roleClaim, setRoleClaim] = useState(() => settings?.role_claim || '');

  const { mutateAsync: updateAccount, status: updateStatus } = useModifyAccount(
    'update',
    email
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const existingSettings = ssoAccount.settings || {};

    await updateAccount({
      username: ssoAccount.member,
      config: ssoAccount.config,
      key: ssoAccount.key,
      settings: {
        ...existingSettings,
        default_sso_role: defaultRole,
        role_claim: roleClaim || undefined,
      },
    });

    onClose();
  };

  return (
    <form
      className="flex flex-col space-y-4"
      id="sso-edit-form"
      onSubmit={handleSubmit}
    >
      <Inputs
        inputs={[
          {
            label: 'Default Role for SSO Users',
            value: defaultRole,
            placeholder: 'Select a default role',
            name: 'defaultRole',
            type: Input.Type.SELECT,
            options: ROLE_OPTIONS.map(opt => ({
              value: opt.value,
              label: opt.label,
            })),
            required: true,
          },
          {
            label: 'Role Claim Name (Optional)',
            value: roleClaim,
            placeholder: 'e.g. app_role',
            name: 'roleClaim',
          },
        ]}
        onChange={values => {
          if ('defaultRole' in values)
            setDefaultRole(values.defaultRole as string);
          if ('roleClaim' in values) setRoleClaim(values.roleClaim as string);
        }}
      />
      {updateStatus === 'pending' && (
        <p className="text-chariot-text-secondary text-sm">Saving...</p>
      )}
    </form>
  );
}
